import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "api/baseurl";
import { toast } from "react-toastify";
import {  MenuItem } from "@mui/material";

import "react-toastify/dist/ReactToastify.css";
import {
  Grid,
  Box,
  Typography,
  Card,
  Autocomplete,
  TextField,
} from "@mui/material";
import { keyframes } from "@mui/system";

import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { encryptBody } from "encryptionAndDecryption/bodyEncryption";
import { decryptBody } from "encryptionAndDecryption/bodyDecryption";
import { fetchSecretKey } from "encryptionAndDecryption/secretKeyDecryption";
import MedicalAbstractBackground from "./MedicalAbstractBackground ";

// Dummy Custom Stepper Component
const CustomStepper = ({ title }) => (
  <Box mb={3} display="flex" justifyContent="center">
    <Typography
      variant="h6"
      sx={{ fontWeight: "bold", color: "#2e7fae", textAlign: "center" }}
    >
      {title}
    </Typography>
  </Box>
);

function AddDeviceData() {
  const loginData = JSON.parse(localStorage.getItem("loginData"));
  const userid = loginData?.userid;
  const date = new Date();
  let day = date.getDate().toString().padStart(2, "0");
  let month = (date.getMonth() + 1).toString().padStart(2, "0");
  let year = date.getFullYear();
  let currentDate = `${year}-${month}-${day}`;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Patients
  const [patients, setPatients] = useState([]);
  const [patientInput, setPatientInput] = useState("");

  // Devices
  const [devices, setDevices] = useState([]);
  const [deviceInput, setDeviceInput] = useState("");

  // ✅ Mapping details (deviceid is single, not array)
  const [mappingDetails, setMappingDetails] = useState({
    patientid: "",
    patient: null, // keep full patient object for dropdown
    deviceid: "",
    device: null, // keep full device object for dropdown
    datatype: "",
    datavalue: "",
    unit: "",
    recordedAt: "",
    source: "",
    assignedDate: currentDate,
  });

  // 🔹 Fetch patients
  const fetchPatients = async (searchTerm = "") => {
    setLoading(true);
    try {
      const secretKey = await fetchSecretKey();
      const token = JSON.parse(localStorage.getItem("loginData"))?.token;

      let response;
      if (searchTerm.trim()) {
        response = await axios.post(
          `/api/patientregistration/searchByCcid/${userid}`,
          { searchTerm },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        response = await axios.get(
          `/api/patientregistration/ccid/nopagination/${userid}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      if (response.status === 200) {
        const decrypted = decryptBody(response.data, secretKey);

        let patientsArray = [];
        if (Array.isArray(decrypted)) {
          patientsArray = decrypted; // no-pagination API
        } else if (decrypted?.content && Array.isArray(decrypted.content)) {
          patientsArray = decrypted.content; // search API
        }

        setPatients(patientsArray);
      }
    } catch (err) {
      console.error("Error fetching patients:", err);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Fetch devices
  const fetchDevices = async (searchTerm = "") => {
    setLoading(true);
    try {
      const secretKey = await fetchSecretKey();
      const token = JSON.parse(localStorage.getItem("loginData"))?.token;

      let response;
      if (searchTerm.trim()) {
        response = await axios.post(
          `/api/device/searchByCcid/${userid}`,
          { searchTerm },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        response = await axios.get(`/api/device`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      if (response.status === 200) {
        const decrypted = decryptBody(response.data, secretKey);
        setDevices(Array.isArray(decrypted) ? decrypted : []);
      }
    } catch (err) {
      console.error("Error fetching devices:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch patients on load
  useEffect(() => {
    fetchPatients();
  }, []);

  // Patient search debounce
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchPatients(patientInput.trim() ? patientInput : "");
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [patientInput]);

  // Device search debounce
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchDevices(deviceInput);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [deviceInput]);

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!mappingDetails.patientid) {
      toast.error("Please select a patient.");
      return;
    }
    if (!mappingDetails.deviceid) {
      toast.error("Please select a device.");
      return;
    }

    setLoading(true);
    try {
      const secretKey = await fetchSecretKey();
      const token = JSON.parse(localStorage.getItem("loginData"))?.token;

      // ✅ Single object payload (not array)
      const rawPayload = {
        ccid:userid,
        patientid: mappingDetails.patientid,
        deviceid: mappingDetails.deviceid,
        datatype: mappingDetails.datatype,
        datavalue: mappingDetails.datavalue,
        unit: mappingDetails.unit,
        recordedAt: mappingDetails.recordedAt,
        source: mappingDetails.source,
        // assignedDate: mappingDetails.assignedDate,
      };

      const encryptedData = encryptBody(rawPayload, secretKey);

      const response = await axios.post(
        "/api/devicedata/create",
        encryptedData,
        {
          headers: {
            "Content-Type": "text/plain",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        const decrypted = decryptBody(response.data, secretKey);
        toast.success(
          decrypted?.message || "Device Data saved successfully!"
        );
        navigate(-1);
      }
    } catch (err) {
      console.error("Error saving mapping:", err);
    //   toast.error("Failed to save mapping");
    } finally {
      setLoading(false);
    }
  };

  // Spinner animation
  const spinAnimation = keyframes`
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  `;
  const dotPositions = [
    { top: "5%", left: "50%" },
    { top: "20%", left: "80%" },
    { top: "50%", left: "95%" },
    { top: "80%", left: "80%" },
    { top: "95%", left: "50%" },
    { top: "80%", left: "20%" },
    { top: "50%", left: "5%" },
    { top: "20%", left: "20%" },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MedicalAbstractBackground/>
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: 80,
              height: 80,
              animation: `${spinAnimation} 1.5s linear infinite`,
            }}
          >
            {dotPositions.map((pos, index) => (
              <Box
                key={index}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "#1A237E",
                  position: "absolute",
                  ...pos,
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      <Card sx={{ width: "1000px", padding: 3, margin: "auto" }}>
        <MDBox pt={4} pb={3} px={3}>
          <CustomStepper title="Device-Patient Mapping" />

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {/* 🔹 Patient Dropdown with Search */}
              <Grid item xs={6}>
                <Autocomplete
                  options={Array.isArray(patients) ? patients : []}
                  getOptionLabel={(option) =>
                    option ? `${option.firstname} ${option.lastname}` : ""
                  }
                  value={mappingDetails.patient}
                  onChange={(e, newValue) =>
                    setMappingDetails((prev) => ({
                      ...prev,
                      patient: newValue,
                      patientid: newValue?.id || "",
                    }))
                  }
                  inputValue={patientInput}
                  onInputChange={(e, newInputValue) =>
                    setPatientInput(newInputValue)
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Search Patient" />
                  )}
                />
              </Grid>

              {/* 🔹 Device Dropdown (Single select now) */}
              <Grid item xs={6}>
                <Autocomplete
                  options={Array.isArray(devices) ? devices : []}
                  getOptionLabel={(option) =>
                    option ? option.devicename || option.name : ""
                  }
                  value={mappingDetails.device}
                  onChange={(e, newValue) =>
                    setMappingDetails((prev) => ({
                      ...prev,
                      device: newValue,
                      deviceid: newValue?.id || "",
                    }))
                  }
                  inputValue={deviceInput}
                  onInputChange={(e, newInputValue) =>
                    setDeviceInput(newInputValue)
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Search Device" />
                  )}
                />
              </Grid>

              {/* 🔹 Assign Date */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Assign Date"
                  InputLabelProps={{ shrink: true }}
                  value={mappingDetails.assignedDate}
                  onChange={(e) =>
                    setMappingDetails((prev) => ({
                      ...prev,
                      assignedDate: e.target.value,
                    }))
                  }
                />
              </Grid>

  <Grid item xs={6}>
    <TextField
      select
      fullWidth
      label="Data Type"
      value={mappingDetails.datatype}
      onChange={(e) =>
        setMappingDetails((prev) => ({
          ...prev,
          datatype: e.target.value,
        }))
      }
       sx={{
      "& .MuiInputBase-root": {
        height: "45px",  
      },
    }}
    >
      <MenuItem value="DEVICE_ERROR">DEVICE_ERROR</MenuItem>
      <MenuItem value="BATTERY_LOW">BATTERY_LOW</MenuItem>
      <MenuItem value="OFFLINE">OFFLINE</MenuItem>
      <MenuItem value="SYSTOLIC_BP">SYSTOLIC_BP</MenuItem>
      <MenuItem value="DIASTOLIC_BP">DIASTOLIC_BP</MenuItem>
      <MenuItem value="GLUCOSE">GLUCOSE</MenuItem>
    </TextField>
  </Grid>

              {/* 🔹 Data Value */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Data Value"
                  value={mappingDetails.datavalue}
                  onChange={(e) =>
                    setMappingDetails((prev) => ({
                      ...prev,
                      datavalue: e.target.value,
                    }))
                  }
                />
              </Grid>

              {/* 🔹 Unit */}
              <Grid item xs={6}>
    <TextField
      select
      fullWidth
      label="Unit"
      value={mappingDetails.unit}
      onChange={(e) =>
        setMappingDetails((prev) => ({
          ...prev,
          unit: e.target.value,
        }))
      }
      sx={{
      "& .MuiInputBase-root": {
        height: "45px",  
      },
    }}
    >
      <MenuItem value="mmHg">mmHg</MenuItem>
      <MenuItem value="mg/dL">mg/dL</MenuItem>
      <MenuItem value="%">%</MenuItem>
      <MenuItem value="°C">°C</MenuItem>
    </TextField>
  </Grid>


              {/* 🔹 Recorded At */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Recorded At"
                  InputLabelProps={{ shrink: true }}
                  value={mappingDetails.recordedAt}
                  onChange={(e) =>
                    setMappingDetails((prev) => ({
                      ...prev,
                      recordedAt: e.target.value,
                    }))
                  }
                />
              </Grid>

              {/* 🔹 Source */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Source"
                  value={mappingDetails.source}
                  onChange={(e) =>
                    setMappingDetails((prev) => ({
                      ...prev,
                      source: e.target.value,
                    }))
                  }
                />
              </Grid>
            </Grid>

            {/* 🔹 Buttons */}
            <MDBox mt={3} display="flex" justifyContent="space-between">
              <MDButton
                variant="outlined"
                onClick={() => navigate(-1)}
               sx={{
      background: "#1A237E !important",  // force override gradient bg
      color: "#fff !important",
      boxShadow: "none !important",
      "&:hover": {
        background: "#1A237E !important", // keep same color
        boxShadow: "none !important",
      },
      "&:focus": {
        background:"#1A237E !important",
        boxShadow: "none !important",
        outline: "none",
      },
      "&:active": {
        background: "#1A237E !important",
        boxShadow: "none !important",
      },
    }}
              >
                Cancel
              </MDButton>

              <MDButton
                type="submit"
                variant="contained"
                sx={{
      background: "#1A237E !important",  // force override gradient bg
      color: "#fff !important",
      boxShadow: "none !important",
      "&:hover": {
        background: "#1A237E !important", // keep same color
        boxShadow: "none !important",
      },
      "&:focus": {
        background:"#1A237E !important",
        boxShadow: "none !important",
        outline: "none",
      },
      "&:active": {
        background: "#1A237E !important",
        boxShadow: "none !important",
      },
    }}
              >
                Submit
              </MDButton>
            </MDBox>
          </form>
        </MDBox>
      </Card>
    </DashboardLayout>
  );
}

export default AddDeviceData;


